import styled from 'styled-components';

/**
 * Layout primitives.
 *
 * Kubit supplies the components — buttons, inputs, type — but not the boxes
 * they sit in, so spacing and arrangement live here. styled-components is
 * already in the tree as Kubit's styling engine, so this adds no dependency
 * and inherits the same theme.
 */

export const Page = styled.main`
  max-width: 60rem;
  min-height: 100dvh;
  margin: 0 auto;
  padding: 2rem 1.25rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const Card = styled.section`
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.6);
`;

export const Stack = styled.div<{ $gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? '0.75rem'};
`;

export const Row = styled.div<{ $gap?: string; $direction?: string; $wrap?: boolean; $align?: string; $height?: string; $width?: string }>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction ?? 'row'};
  align-items: ${({ $align }) => $align ?? 'center'};
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  gap: ${({ $gap }) => $gap ?? '0.75rem'};
  height: ${({ $height }) => $height ?? 'auto'};
  width: ${({ $width }) => $width ?? '100%'};
`;

export const Spread = styled(Row)`
  justify-content: space-between;
`;

/** Two columns on desktop, stacked on narrow screens. */
export const Columns = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;

  @media (max-width: 48rem) {
    grid-template-columns: 1fr;
  }
`;

/** The voting deck: cards wrap and stay finger-sized on a phone. */
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(4.5rem, 1fr));
  gap: 0.75rem;
`;

/**
 * Wrapper for Kubit's `Image`, which renders its `<img>` inside a `<figure>`
 * and so inherits the browser's default 1em/40px figure margin. Anywhere the
 * image has to line up with the text around it, that margin is wrong.
 */
export const PlainImage = styled.div`
  figure {
    margin: 0;
  }
`;

/**
 * Small helper line under a field — an input's allowed range, say. Kubit's
 * `Text` variants are theme-driven and don't expose an arbitrary size, so this
 * is a plain styled span pinned at a legible 0.75rem.
 */
export const Hint = styled.span`
  font-size: 0.75rem;
  line-height: 1.3;
  opacity: 0.7;
`;
